import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Pagination, Typography, useTheme } from '@mui/material';
import MessagesSnackbar from '../../components/shared/MessagesSnackbar';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../components/shared/useUser';
import { StorefrontBanner } from '../ContentManagement/Banners/types/BannerAdmin';
import { bannerService } from '../../app/services/api/content/bannerService';
import { client, Category, ContainerFillResult, Item, OrderDetails, OrderItemDetails, OrderlySystem, ShippingDetailsRequest } from '../../app/services/api/orderManagementClient';
import { OrderStatus } from '../../enums/OrderStatus';
import { PromotionForCustomerDto } from '../../app/services/api/promotions';
import dayjs from 'dayjs';
import { useActiveOrder } from '../../contexts/ActiveOrderContext';
import useCurrencyCodeConfiguration from '../../hooks/configurations/useCurrencyCodeConfiguration';
import useNswagClient from '../../hooks/api/useNswagClient';
import { normalizeDeliveryDate } from '../../hooks/useDeliveryPricing';
import { DateOnly } from '../../app/services/api/ofdClient';
import StorefrontHeader from './components/StorefrontHeader';
import CategorySidebar from './components/CategorySidebar';
import HeroBannerCarousel from './components/HeroBannerCarousel';
import PromoBannerCard from './components/PromoBannerCard';
import ProductCard from './components/ProductCard';
import { BANNER_DESIGN_HEIGHT, BANNER_DESIGN_WIDTH } from '../ContentManagement/Banners/components/BannerPreview';

const PAGE_SIZE = 12;
const STOREFRONT_HEADER_HEIGHT = 44;
const HERO_BANNER_FIXED_HEIGHT = BANNER_DESIGN_HEIGHT;
const HERO_SECTION_GAP = 16;
const QUANTITY_SYNC_DEBOUNCE_MS = 400;

const isPromoOrderItem = (item: Pick<OrderItemDetails, 'promotionAllocationId' | 'isPromoLine'>) =>
  item.promotionAllocationId != null || item.isPromoLine === true;

const isRestorableActiveOrderStatus = (status?: string) => status === OrderStatus.Pending;

const buildOptimisticOrderItem = (product: Item, quantity: number): OrderItemDetails => ({
  id: -product.id,
  itemId: product.id,
  sku: product.sku,
  name: product.name,
  primaryUom: product.primaryUom,
  orderableUom: product.orderableUom,
  price: product.price,
  gbpPrice: product.gbpPrice,
  categoryId: product.categoryId,
  packSize: product.packSize,
  category: product.category,
  stockOnHand: product.stockOnHand,
  stockOnOrder: product.stockOnOrder,
  quantity,
  confirmedQuantity: 0,
  shippedQuantity: 0,
  imageId: product.imageId,
  thumbnailUrl: product.thumbnailUrl,
  maxQuantity: product.maxQuantity,
  minQuantity: product.minQuantity,
  gtin: product.gtin,
  isPromoLine: false,
});

const applyOptimisticQuantity = (order: OrderDetails, product: Item, quantity: number): OrderDetails => {
  if (quantity <= 0) {
    return { ...order, items: order.items.filter((i) => i.itemId !== product.id || isPromoOrderItem(i)) };
  }
  const existingItem = order.items?.find((i) => i.itemId === product.id && !isPromoOrderItem(i));
  if (existingItem) {
    return {
      ...order,
      items: order.items.map((i) => (i.itemId === product.id && !isPromoOrderItem(i) ? { ...i, quantity } : i)),
    };
  }
  return { ...order, items: [...order.items, buildOptimisticOrderItem(product, quantity)] };
};

const StorefrontPage: FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation('storefront');
  const { selectedStore } = useContext(UserContext);
  const { activeOrder, setActiveOrder, setContainerData: setActiveContainerData, setShippingDetails } = useActiveOrder();
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();
  const { getRetailerIdByStore, deliveryPricingClient } = useNswagClient();
  const getRetailerIdByStoreRef = useRef(getRetailerIdByStore);
  const deliveryPricingClientRef = useRef(deliveryPricingClient);

  const [banners, setBanners] = useState<StorefrontBanner[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [products, setProducts] = useState<Item[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productPage, setProductPage] = useState(1);
  const [productTotalPages, setProductTotalPages] = useState(1);
  const [productTotalCount, setProductTotalCount] = useState(0);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [orderTypeId, setOrderTypeId] = useState<number>(-1);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [optimisticQuantities, setOptimisticQuantities] = useState<Record<number, number>>({});

  const initialised = useRef(false);
  const activeOrderRef = useRef(activeOrder);
  const optimisticQuantitiesRef = useRef(optimisticQuantities);
  const pendingQuantitiesRef = useRef<Record<number, number>>({});
  const pendingOrderItemIdsRef = useRef<Record<number, number>>({});
  const syncTimersRef = useRef<Record<number, ReturnType<typeof setTimeout>>>({});
  const orderCreationPromiseRef = useRef<Promise<OrderDetails | null> | null>(null);

  useEffect(() => { activeOrderRef.current = activeOrder; }, [activeOrder]);
  useEffect(() => { optimisticQuantitiesRef.current = optimisticQuantities; }, [optimisticQuantities]);
  useEffect(() => {
    getRetailerIdByStoreRef.current = getRetailerIdByStore;
  }, [getRetailerIdByStore]);
  useEffect(() => {
    deliveryPricingClientRef.current = deliveryPricingClient;
  }, [deliveryPricingClient]);
  useEffect(() => () => {
    Object.values(syncTimersRef.current).forEach(clearTimeout);
  }, []);

  const activateShippingMethod = useCallback(async (
    storeNumber: string,
    order: OrderDetails,
    containerData: ContainerFillResult,
    shippingData: ShippingDetailsRequest,
    retailerId?: number,
  ) => {
    try {
      const flatContainerCharge = parseFloat(String(shippingData.deliveryCharge)) || 0;
      const count = containerData.totalContainersUsed > 0 ? containerData.totalContainersUsed : 0;

      let containerCharge = flatContainerCharge;
      let totalChargeValue = flatContainerCharge * count;

      const shippingMethodId = Number(shippingData.deliveryMethodId);
      const normalizedDeliveryDate = normalizeDeliveryDate(
        order.deliveryDate ?? new Date().toISOString().split('T')[0],
      );

      if (containerData.totalPalletsNeeded > 0 && shippingMethodId && normalizedDeliveryDate) {
        try {
          const result = await deliveryPricingClientRef.current.resolve({
            shippingMethodId,
            retailerId,
            wholePallets: Math.floor(containerData.totalPalletsNeeded),
            lastPalletFillPercent: containerData.containerFillPercents.length > 0
              ? containerData.containerFillPercents[containerData.containerFillPercents.length - 1]
              : 100,
            deliveryDate: normalizedDeliveryDate as unknown as DateOnly,
            baseCharge: flatContainerCharge,
          });
          if (result.resolved) {
            totalChargeValue = result.totalCharge ?? 0;
            containerCharge = count > 0 ? totalChargeValue / count : totalChargeValue;
          }
        } catch (engineError) {
          console.error('Failed to resolve delivery pricing', engineError);
        }
      }

      setShippingDetails({
        deliveryMethod: shippingData.deliveryMethod || '',
        containerCharge: containerCharge.toString(),
        totalDeliveryCharge: totalChargeValue.toFixed(2),
      });

      const payload: ShippingDetailsRequest = {
        deliveryCharge: containerCharge,
        totalDeliveryCharge: totalChargeValue,
        deliveryMethod: shippingData.deliveryMethod || '',
        deliveryMethodId: shippingData.deliveryMethodId || '',
        orderStatus: order.statusId.toString() ?? '',
        deliveryOptions: shippingData.deliveryOptions || [],
        country: shippingData.country || '',
        location: shippingData.location || '',
        ...(shippingData.containerType && shippingData.containerType !== 0 && { containerType: shippingData.containerType }),
        ...(shippingData.palletType && shippingData.palletType !== 0 && { palletType: shippingData.palletType }),
      };
      await client.setShippingDetails(storeNumber, order.id.toString(), payload);
    } catch (error) {
      console.error('Failed to activate shipping method', error);
    }
  }, [setShippingDetails]);

  const refreshShippingForOrder = useCallback(async (order: OrderDetails) => {
    if (!selectedStore?.storeNumber || !order.id) return;

    try {
      const containerResult = await client.getStoreContainerData(
        selectedStore.storeNumber,
        order.id.toString(),
      );
      if (!containerResult.data) return;

      setActiveContainerData(containerResult.data);

      const shippingResult = await client.getShippingDetails(
        selectedStore.storeNumber,
        order.id.toString(),
        '',
      );
      if (!shippingResult.success || !shippingResult.data) return;

      const deliveryChargeStr = shippingResult.data.deliveryCharge?.toString() || '0';
      const totalDeliveryChargeNum = parseFloat(shippingResult.data.totalDeliveryCharge?.toString() || '0') || 0;
      const totalDeliveryChargeStr = totalDeliveryChargeNum > 0 ? totalDeliveryChargeNum.toString() : deliveryChargeStr;
      setShippingDetails({
        deliveryMethod: shippingResult.data.deliveryMethod || '',
        containerCharge: deliveryChargeStr,
        totalDeliveryCharge: totalDeliveryChargeStr,
      });

      const retailerId = await getRetailerIdByStoreRef.current(selectedStore.storeNumber).catch(() => undefined);
      await activateShippingMethod(
        selectedStore.storeNumber,
        order,
        containerResult.data,
        shippingResult.data,
        retailerId,
      );
    } catch (error) {
      console.error('Failed to refresh shipping for order', error);
    }
  }, [activateShippingMethod, selectedStore?.storeNumber, setActiveContainerData, setShippingDetails]);

  useEffect(() => {
    getCurrencyCodeConfiguration();
    bannerService.storefront.getBanners()
      .then(setBanners)
      .catch(console.error);
  }, []);

  useEffect(() => {
    const handleFocus = async () => {
      const storedId = sessionStorage.getItem('orderly_active_order_meta');
      if (!storedId || !selectedStore?.storeNumber) return;
      const orderId = parseInt(storedId, 10);
      const refreshed = await client.getOrder(selectedStore.storeNumber, orderId);
      if (refreshed.success && refreshed.data && isRestorableActiveOrderStatus(refreshed.data.status)) {
        setActiveOrder(refreshed.data);
        void refreshShippingForOrder(refreshed.data);
      } else if (refreshed.success && refreshed.data) {
        sessionStorage.removeItem('orderly_active_order_meta');
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refreshShippingForOrder, selectedStore?.storeNumber, setActiveOrder]);

  useEffect(() => {
    if (!selectedStore?.storeNumber) return;
    if (initialised.current) return;
    initialised.current = true;

    const init = async () => {
      try {
        const orderTypesRes = await client.getOrderTypes(selectedStore.storeNumber);
        const types = orderTypesRes.data ?? [];
        const defaultType = types.length > 0 ? types[0].id : -1;
        setOrderTypeId(defaultType);

        setCategoriesLoading(true);
        const categoriesRes = await client.getCategories(selectedStore.storeNumber, defaultType, '');
        setCategories(categoriesRes.data ?? []);
        setCategoriesLoading(false);

        await fetchProducts(selectedStore.storeNumber!, '', null, defaultType);

        if (!activeOrder) {
          try {
            const storedId = sessionStorage.getItem('orderly_active_order_meta');
            if (storedId) {
              const orderId = parseInt(storedId, 10);
              const restored = await client.getOrder(selectedStore.storeNumber, orderId);
              if (restored.success && restored.data && isRestorableActiveOrderStatus(restored.data.status)) {
                setActiveOrder(restored.data);
                void refreshShippingForOrder(restored.data);
              } else {
                sessionStorage.removeItem('orderly_active_order_meta');
              }
            }
          } catch {
            sessionStorage.removeItem('orderly_active_order_meta');
          }
        }
      } catch (err) {
        console.error('Failed to initialise storefront', err);
        setCategoriesLoading(false);
      }
    };
    init();
  }, [selectedStore?.storeNumber]);

  const fetchProducts = async (storeNumber: string, search: string, categoryId: number | null, orderType: number, page: number = 1) => {
    setProductsLoading(true);
    try {
      const apiPageNumber = Math.max(0, page - 1);
      const res = await client.getItems({
        storeNumber,
        item: search || undefined,
        categoryId: categoryId ?? -1,
        orderType,
        supplier: undefined,
        pageNumber: apiPageNumber,
        pageSize: PAGE_SIZE,
        sort: 'Name',
        direction: 'asc',
      });
      setProducts(res.data?.items ?? []);
      const returnedApiPage = res.data?.pageNumber ?? apiPageNumber;
      setProductPage(returnedApiPage + 1);
      setProductTotalPages(res.data?.totalPages ?? 1);
      setProductTotalCount(res.data?.totalCount ?? 0);
    } catch (err) {
      console.error('Failed to fetch products', err);
      setProducts([]);
      setProductPage(1);
      setProductTotalPages(1);
      setProductTotalCount(0);
    } finally {
      setProductsLoading(false);
    }
  };

  const handleCategorySelect = useCallback((catId: number | null) => {
    setSelectedCategoryId(catId);
    if (selectedStore?.storeNumber) {
      fetchProducts(selectedStore.storeNumber, searchValue, catId, orderTypeId, 1);
    }
  }, [selectedStore?.storeNumber, searchValue, orderTypeId]);

  const handleSearch = useCallback(() => {
    if (selectedStore?.storeNumber) {
      fetchProducts(selectedStore.storeNumber, searchValue, selectedCategoryId, orderTypeId, 1);
    }
  }, [selectedStore?.storeNumber, searchValue, selectedCategoryId, orderTypeId]);

  const handleProductsPageChange = useCallback((_: React.ChangeEvent<unknown>, page: number) => {
    if (!selectedStore?.storeNumber) return;
    fetchProducts(selectedStore.storeNumber, searchValue, selectedCategoryId, orderTypeId, page);
  }, [selectedStore?.storeNumber, searchValue, selectedCategoryId, orderTypeId]);

  const resolveOrderForAdd = useCallback(async (): Promise<OrderDetails | null> => {
    if (!selectedStore?.storeNumber) return null;
    if (activeOrderRef.current) return activeOrderRef.current;
    if (orderCreationPromiseRef.current) return orderCreationPromiseRef.current;
    if (orderTypeId < 0) return null;

    orderCreationPromiseRef.current = (async () => {
      try {
        const today = dayjs().format('YYYY-MM-DD');
        const created = await client.createOrder({
          storeNumber: selectedStore.storeNumber!,
          emergencyOrder: true,
          orderTypeId,
          deliveryDate: today,
          cutOffDate: today,
          supplierNumber: '',
          orderlySystem: OrderlySystem.OFD,
          items: [],
        });

        if (!created.success || !created.data) return null;

        const createdOrder = await client.getOrder(selectedStore.storeNumber, created.data);
        if (!createdOrder.success || !createdOrder.data) return null;

        sessionStorage.setItem('orderly_active_order_meta', createdOrder.data.id.toString());
        setActiveOrder(createdOrder.data);
        activeOrderRef.current = createdOrder.data;
        return createdOrder.data;
      } finally {
        orderCreationPromiseRef.current = null;
      }
    })();

    return orderCreationPromiseRef.current;
  }, [orderTypeId, selectedStore?.storeNumber, setActiveOrder]);

  const getDisplayQuantity = useCallback((productId: number) => {
    const fromOrder = activeOrder?.items?.find((i) => i.itemId === productId && !isPromoOrderItem(i))?.quantity;
    if (fromOrder !== undefined) return fromOrder;
    return optimisticQuantities[productId] ?? 0;
  }, [activeOrder, optimisticQuantities]);

  const syncProductQuantity = useCallback(async (product: Item, quantity: number) => {
    if (!selectedStore?.storeNumber) return;

    const rollbackOrder = activeOrderRef.current;
    const rollbackOptimistic = optimisticQuantitiesRef.current[product.id];
    const savedOrderItemId = pendingOrderItemIdsRef.current[product.id];

    try {
      if (quantity <= 0 && !rollbackOrder?.items?.some((i) => i.itemId === product.id && !isPromoOrderItem(i)) && savedOrderItemId === undefined) {
        delete pendingQuantitiesRef.current[product.id];
        return;
      }

      const order = quantity > 0 ? await resolveOrderForAdd() : activeOrderRef.current;
      if (!order) {
        throw new Error('Unable to resolve order');
      }

      const serverItem = order.items?.find((i) => i.itemId === product.id && !isPromoOrderItem(i));
      const orderItemId = savedOrderItemId ?? serverItem?.id;
      let apiResult: { success: boolean; message?: string | undefined };

      if (quantity <= 0 && orderItemId !== undefined && orderItemId > 0) {
        apiResult = await client.deleteOrderItem(
          selectedStore.storeNumber,
          order.id.toString(),
          orderItemId.toString(),
        );
      } else if (serverItem && serverItem.id > 0) {
        apiResult = await client.updateOrder(selectedStore.storeNumber, {
          id: order.id,
          orderlySystem: OrderlySystem.OFD,
          items: order.items.map((i) => ({
            id: i.itemId,
            quantity: i.itemId === product.id && !isPromoOrderItem(i) ? quantity : i.quantity,
            isPromoLine: i.isPromoLine ?? false,
          })),
        });
      } else if (quantity > 0) {
        apiResult = await client.addOrderItem(selectedStore.storeNumber, {
          orderId: order.id,
          itemId: product.id,
          quantity,
          predictedQuantity: quantity,
        });
      } else {
        apiResult = { success: true };
      }

      if (!apiResult.success) {
        throw new Error(apiResult.message ?? 'Update failed');
      }

      const refreshedOrder = await client.getOrder(selectedStore.storeNumber, order.id);
      if (refreshedOrder.success && refreshedOrder.data) {
        setActiveOrder(refreshedOrder.data);
        activeOrderRef.current = refreshedOrder.data;
      }

      delete pendingOrderItemIdsRef.current[product.id];
      setOptimisticQuantities((prev) => {
        const next = { ...prev };
        delete next[product.id];
        return next;
      });

      const latest = pendingQuantitiesRef.current[product.id];
      if (latest !== undefined && latest !== quantity) {
        await syncProductQuantity(product, latest);
        return;
      }

      delete pendingQuantitiesRef.current[product.id];

      const orderForShipping = activeOrderRef.current ?? refreshedOrder.data;
      if (orderForShipping) {
        void refreshShippingForOrder(orderForShipping);
      }
    } catch {
      const orderId = activeOrderRef.current?.id ?? rollbackOrder?.id;
      if (orderId) {
        const refreshed = await client.getOrder(selectedStore.storeNumber, orderId);
        if (refreshed.success && refreshed.data) {
          let order = refreshed.data;
          const latest = pendingQuantitiesRef.current[product.id];
          if (latest !== undefined) {
            order = applyOptimisticQuantity(order, product, latest);
          }
          setActiveOrder(order);
          activeOrderRef.current = order;
        } else if (rollbackOrder) {
          setActiveOrder(rollbackOrder);
          activeOrderRef.current = rollbackOrder;
        }
      } else {
        setActiveOrder(null);
        activeOrderRef.current = null;
        setOptimisticQuantities((prev) => {
          const next = { ...prev };
          if (rollbackOptimistic !== undefined) next[product.id] = rollbackOptimistic;
          else delete next[product.id];
          return next;
        });
      }

      setSnackbarMessage(t('addToDeliveryError'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  }, [refreshShippingForOrder, resolveOrderForAdd, selectedStore?.storeNumber, setActiveOrder, t]);

  const scheduleQuantitySync = useCallback((product: Item, targetQuantity: number) => {
    pendingQuantitiesRef.current[product.id] = targetQuantity;

    if (syncTimersRef.current[product.id]) {
      clearTimeout(syncTimersRef.current[product.id]);
    }

    syncTimersRef.current[product.id] = setTimeout(() => {
      delete syncTimersRef.current[product.id];
      const quantity = pendingQuantitiesRef.current[product.id];
      if (quantity === undefined) return;
      void syncProductQuantity(product, quantity);
    }, QUANTITY_SYNC_DEBOUNCE_MS);
  }, [syncProductQuantity]);

  const handleQuantityChange = useCallback((product: Item, targetQuantity: number) => {
    if (targetQuantity < 0) return;

    const currentOrder = activeOrderRef.current;

    if (currentOrder) {
      const existingItem = currentOrder.items?.find((i) => i.itemId === product.id && !isPromoOrderItem(i));
      if (existingItem && targetQuantity <= 0) {
        pendingOrderItemIdsRef.current[product.id] = existingItem.id;
      } else if (targetQuantity > 0) {
        delete pendingOrderItemIdsRef.current[product.id];
      }

      const updated = applyOptimisticQuantity(currentOrder, product, targetQuantity);
      setActiveOrder(updated);
      activeOrderRef.current = updated;
      setOptimisticQuantities((prev) => {
        const next = { ...prev };
        delete next[product.id];
        return next;
      });
    } else if (targetQuantity > 0) {
      setOptimisticQuantities((prev) => ({ ...prev, [product.id]: targetQuantity }));
    } else {
      setOptimisticQuantities((prev) => {
        const next = { ...prev };
        delete next[product.id];
        return next;
      });
      delete pendingQuantitiesRef.current[product.id];
      delete pendingOrderItemIdsRef.current[product.id];
      if (syncTimersRef.current[product.id]) {
        clearTimeout(syncTimersRef.current[product.id]);
        delete syncTimersRef.current[product.id];
      }
      return;
    }

    scheduleQuantitySync(product, targetQuantity);
  }, [scheduleQuantitySync, setActiveOrder]);

  const handleAddToOrder = useCallback((product: Item) => {
    const step = Math.max(1, product.minQuantity ?? 1);
    handleQuantityChange(product, getDisplayQuantity(product.id) + step);
  }, [getDisplayQuantity, handleQuantityChange]);

  const handleDecreaseFromOrder = useCallback((product: Item) => {
    const step = Math.max(1, product.minQuantity ?? 1);
    const current = getDisplayQuantity(product.id);
    if (current <= 0) return;
    handleQuantityChange(product, Math.max(0, current - step));
  }, [getDisplayQuantity, handleQuantityChange]);

  const handleSetQuantity = useCallback((product: Item, quantity: number) => {
    handleQuantityChange(product, quantity);
  }, [handleQuantityChange]);

  const getPromoDisplayQuantity = useCallback((productId: number) => {
    return activeOrder?.items?.find((i) => i.itemId === productId && isPromoOrderItem(i))?.quantity ?? 0;
  }, [activeOrder]);

  const handlePromoQuantityChange = useCallback(async (product: Item, quantity: number): Promise<{ success: boolean; message?: string }> => {
    if (!selectedStore?.storeNumber) return { success: false, message: t('addToDeliveryError') };

    try {
      const order = quantity > 0 ? await resolveOrderForAdd() : activeOrderRef.current;
      if (!order) return { success: false, message: t('addToDeliveryError') };

      const existingPromoItem = order.items?.find((i) => i.itemId === product.id && isPromoOrderItem(i));

      const result = await client.addOrderItem(selectedStore.storeNumber, {
        orderId: order.id,
        itemId: product.id,
        quantity,
        isPromoLine: true,
        promoUnitPrice: existingPromoItem?.promoUnitPrice ?? existingPromoItem?.price,
        promotionAllocationId: existingPromoItem?.promotionAllocationId ?? undefined,
      });

      if (!result.success) {
        return { success: false, message: result.message ?? t('addToDeliveryError') };
      }

      const refreshed = await client.getOrder(selectedStore.storeNumber, order.id);
      if (refreshed.success && refreshed.data) {
        setActiveOrder(refreshed.data);
        activeOrderRef.current = refreshed.data;
        void refreshShippingForOrder(refreshed.data);
      }

      return { success: true };
    } catch {
      return { success: false, message: t('addToDeliveryError') };
    }
  }, [refreshShippingForOrder, resolveOrderForAdd, selectedStore?.storeNumber, setActiveOrder, t]);

  const getPromotionForItem = useCallback((item: Item): PromotionForCustomerDto | undefined => {
    if (!item.promotionId || item.promotionRemainingQuantity == null) {
      return undefined;
    }
    return {
      promotionId: item.promotionId,
      contentId: item.id,
      name: item.promotionName ?? '',
      pricingType: item.promotionPricingType ?? (item.promotionFixedPromoUnitPrice != null ? 2 : 1),
      discountPercentage: item.promotionDiscountPercentage ?? null,
      fixedPromoUnitPrice: item.promotionFixedPromoUnitPrice ?? null,
      quantityCap: item.promotionQuantityCap ?? item.promotionRemainingQuantity,
      remainingQuantity: item.promotionRemainingQuantity,
      reservationDurationMinutes: item.promotionReservationDurationMinutes ?? 0,
      promoDescription: item.promotionDescription ?? null,
      startDateUtc: item.promotionStartDateUtc ?? '',
      endDateUtc: item.promotionEndDateUtc ?? '',
      tagName: null,
      tagTextColor: null,
      tagBackgroundColor: null,
      tagIconKey: null,
    };
  }, []);

  const handleCartClick = () => {
    if (!activeOrder) return;
    navigate(`/order/${activeOrder.id}`);
  };

  const cartItemCount = useMemo(() => {
    const itemIds = new Set<number>();
    activeOrder?.items?.forEach((i) => itemIds.add(i.itemId));
    Object.keys(optimisticQuantities).forEach((id) => itemIds.add(Number(id)));

    let count = 0;
    itemIds.forEach((id) => {
      const normalQty = getDisplayQuantity(id);
      const promoQty = getPromoDisplayQuantity(id);
      if (normalQty > 0 || promoQty > 0) count += 1;
    });
    return count;
  }, [activeOrder, optimisticQuantities, getDisplayQuantity, getPromoDisplayQuantity]);

  const slotGroups = banners.reduce<Record<string, typeof banners>>((acc, b) => {
    if (!acc[b.slotKey]) acc[b.slotKey] = [];
    acc[b.slotKey].push(b);
    return acc;
  }, {});
  const sortedSlotKeys = Object.keys(slotGroups).sort(
    (a, b) => (slotGroups[b][0].width ?? 0) - (slotGroups[a][0].width ?? 0),
  );
  const heroBanners = sortedSlotKeys.length > 0 ? slotGroups[sortedSlotKeys[0]] : [];
  const promoLeftBanners = sortedSlotKeys.length > 1 ? slotGroups[sortedSlotKeys[1]] : [];
  const promoRightBanners = sortedSlotKeys.length > 2 ? slotGroups[sortedSlotKeys[2]] : [];
  const hero0 = heroBanners[0];
  const heroHeight = heroBanners.length > 0
    ? (hero0.width > 0 && hero0.height > 0
      ? Math.round(BANNER_DESIGN_WIDTH * hero0.height / hero0.width)
      : hero0.height || HERO_BANNER_FIXED_HEIGHT)
    : HERO_BANNER_FIXED_HEIGHT;
  const sidebarFixedHeight = heroBanners.length > 0
    ? STOREFRONT_HEADER_HEIGHT + HERO_SECTION_GAP + heroHeight
    : undefined;

  return (
    <Box sx={{ background: 'linear-gradient(180deg, #EFEFEF 0px, #FFFFFF 220px)', minHeight: '100vh', p: { xs: '16px', md: '24px' } }}>
      <MessagesSnackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        severity={snackbarSeverity}
        duration={3000}
      />
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: '15px', mb: '20px' }}>
        <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
          <CategorySidebar
            categories={categories}
            loading={categoriesLoading}
            selectedCategoryId={selectedCategoryId}
            fixedHeight={sidebarFixedHeight}
            onCategorySelect={handleCategorySelect}
          />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '15px' }}>
          <Box sx={{ height: `${STOREFRONT_HEADER_HEIGHT}px` }}>
            <StorefrontHeader
              searchValue={searchValue}
              onSearchChange={setSearchValue}
              onSearchSubmit={handleSearch}
              cartItemCount={cartItemCount}
              onCartClick={handleCartClick}
              cartDisabled={!activeOrder}
            />
          </Box>
          <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
            <CategorySidebar
              categories={categories}
              loading={categoriesLoading}
              selectedCategoryId={selectedCategoryId}
              onCategorySelect={handleCategorySelect}
            />
          </Box>
          {heroBanners.length > 0 && (
            <Box sx={{ flex: 1 }}>
              <HeroBannerCarousel banners={heroBanners}
                fixedHeight={heroHeight} />
            </Box>
          )}
        </Box>
      </Box>

      {(promoLeftBanners.length > 0 || promoRightBanners.length > 0) && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '15px', mb: '32px' }}>
          {promoLeftBanners.length > 0 && <PromoBannerCard banners={promoLeftBanners} />}
          {promoRightBanners.length > 0 && <PromoBannerCard banners={promoRightBanners} />}
        </Box>
      )}

      <Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '12px', mb: '20px' }}>
          <Typography sx={{ fontWeight: 600, fontSize: '20px', lineHeight: 1.2 }}>
            {selectedCategoryId
              ? categories.find((c) => c.id === selectedCategoryId)?.name ?? t('editorsPick')
              : t('editorsPick')}
          </Typography>
          {!selectedCategoryId && (
            <Typography
              sx={{ color: theme.palette.custom.gray[400], fontStyle: 'italic', fontSize: '14px' }}
            >
              {t('chosenForYou')}
            </Typography>
          )}
        </Box>

        {productsLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: '48px' }}>
            <CircularProgress />
          </Box>
        )}

        {!productsLoading && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
              gap: { xs: '12px', md: '16px', lg: '18px' },
            }}
          >
            {products.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                currencyCode={currencyCode}
                basketQuantity={getDisplayQuantity(product.id)}
                promoQuantity={getPromoDisplayQuantity(product.id)}
                promotion={getPromotionForItem(product)}
                onAddToOrder={handleAddToOrder}
                onDecreaseFromOrder={handleDecreaseFromOrder}
                onSetQuantity={handleSetQuantity}
                onSetPromoQuantity={handlePromoQuantityChange}
              />
            ))}
          </Box>
        )}

        {!productsLoading && products.length === 0 && (
          <Typography
            sx={{ color: theme.palette.custom.gray[400], textAlign: 'center', py: '48px', fontSize: '14px' }}
          >
            {t('noProductsFound')}
          </Typography>
        )}

        {!productsLoading && productTotalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={productTotalPages}
              page={productPage}
              onChange={handleProductsPageChange}
              shape="rounded"
              color="primary"
              size="medium"
            />
          </Box>
        )}

        {!productsLoading && productTotalCount > 0 && (
          <Typography sx={{ color: theme.palette.custom.gray[400], textAlign: 'center', mt: 1, fontSize: '12px' }}>
            {productTotalCount}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default StorefrontPage;
